/* eslint-disable react/jsx-sort-props */

'use client';

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import * as maptilersdk from '@maptiler/sdk';
import generateUniqueId from 'generate-unique-id';
import { Feature, MultiPolygon, Polygon } from 'geojson';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { env } from 'next-runtime-env';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Map } from 'react-map-gl/maplibre';
import { useDispatch, useSelector } from 'react-redux';

import { useHandleInitialMapboxZooming } from '@/components/page-components/plat-map/mapbox/useHandleInitialMapboxZooming';
import { AcresFeatureFlags } from '@/constants/constants';
import { setupDrawCircleMode } from '@/features/draw/modes/customCircle';
import { setupDrawPivot } from '@/features/draw/modes/customPivot';
import { setupDrawPolygon } from '@/features/draw/modes/draw-polygon';
import { setupDrawRectangleDrag } from '@/features/draw/modes/rectangle-drag-mode';
import getMapboxDrawStyles from '@/features/draw/styles';
import { AllMapboxLayers } from '@/features/mapbox/allMapboxLayers';
import { useToolbarContext } from '@/features/toolbar/provider';
import useMapCustomizationSelections from '@/hooks/map-box/useMapCustomizationSelections';
import useUpdatedRef from '@/hooks/useUpdatedRef';
import {
  advancedSearchParcelDots,
  advancedSearchParcelFill,
  advancedSearchParcelLine,
} from '@/mapbox-layers/corelogic';
import drawFillLayer from '@/mapbox-layers/draw';
import { savedBoundariesLayer } from '@/mapbox-layers/parcels/saved-boundaries';
import { isBoundaryFeature } from '@/models/features/guards';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import { useInitializeContext } from '@/providers/InitializeProvider';
import {
  MAX_2D_MODE_ZOOM,
  MIN_2D_MODE_ZOOM,
  useMapboxContext,
} from '@/providers/mapbox/MapboxProvider';
import { useToolTipContext } from '@/providers/ToolTipProvider';
import { setDrawTool } from '@/redux/slices/drawV2Slice';
import { LayerIdEnums } from '@/utils/convertActiveLayer';
import { isDefined } from '@/utils/guards';

import locationPin from '../../../../../public/img/rejection-pin.png';
import useActiveSource from '../../../../hooks/map-box/useActiveSource';
import useAdvancedSearchResultSelect from '../../../../hooks/map-box/useAdvancedSearchResultSelect';
import useMapFeatureHover from '../../../../hooks/map-box/useMapFeatureHover';
import useMapSelections from '../../../../hooks/map-box/useMapSelections';
import useSelectionCentroid from '../../../../hooks/map-box/useSelectionCentroid';
import { LocationPinLayer } from '../../../../mapbox-layers/location';
import useZoomSelect from '../../../../mapbox-layers/zoomSelect';
import { AcresFeature } from '../../../../models/features';
import { RootState } from '../../../../redux/reducers';
import { clearBoundaryTransfer } from '../../../../redux/slices/boundaryTransfer';
import { addFeature } from '../../../../redux/slices/featureSlice';
import addLayer from '../../../../utils/addLayer';
import dynamicURl, {
  EXCLUDE_DYNAMIC_URL_LAYERS,
} from '../../../../utils/dynamicURL';
import { isPolygonic } from '../../../../utils/geospatial-tools';
import Logger from '../../../../utils/logger';
import { multiUnion } from '../../../../utils/multiUnion';
import zoomToBounds from '../../../../utils/zoomToBounds';

/** Don't load parcel fill or clu fill layers on load, this gets handled in map settings */
const MAP_BOX_LAYERS = [
  advancedSearchParcelDots,
  advancedSearchParcelLine,
  advancedSearchParcelFill,
  drawFillLayer,
  savedBoundariesLayer,
  // DONE: Moved to allMapboxLayers.tsx
  // selectionFillLayer,
  // selectionLineLayer,
  // selectionDotLayer,
  // boundaryFillLayer,
];

interface Props {}

maptilersdk.config.apiKey = env('NEXT_PUBLIC_MAPTILER_TOKEN') ?? '';

const MapBox: FunctionComponent<Props> = () => {
  const { setIsVisible, setText } = useToolTipContext();
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const { boundaryTransfer } = useSelector(
    (state: RootState) => state.boundaryTransfer
  );
  const { getFeature } = useInitializeContext();
  const isToolbarActive = getFeature(AcresFeatureFlags.TEMP_TOOL_TOP_BAR);
  const accessToken = env('NEXT_PUBLIC_MAPBOX_TOKEN') || '';
  const mapTilerAccessToken = env('NEXT_PUBLIC_MAPTILER_TOKEN') || '';

  const { activeFileId } = useFileManagementContext();
  const {
    setIsCursorOverMap,
    setIsMapInstanceLoaded,
    isMapInstanceLoaded,
    layers: { mapboxBaseMap },
    mapInstance: map,
  } = useMapboxContext();

  const { allFeatures } = useSelector((state: RootState) => state.features);

  const { drawTool: drawToolRedux } = useSelector(
    (state: RootState) => state.drawV2
  );

  const { drawTool: toolbarDrawTool } = useToolbarContext();

  const drawTool = useMemo(
    () => (isToolbarActive ? toolbarDrawTool : drawToolRedux),
    [drawToolRedux, isToolbarActive, toolbarDrawTool]
  );

  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const dispatch = useDispatch();
  const boundaryTransferRef = useRef(boundaryTransfer);
  const drawToolRef = useUpdatedRef(drawTool);

  const lat =
    searchParams?.get('lat') && !Number.isNaN(Number(searchParams.get('lat')))
      ? Number(searchParams.get('lat'))
      : undefined;
  const lng =
    searchParams?.get('lng') && !Number.isNaN(Number(searchParams.get('lng')))
      ? Number(searchParams.get('lng'))
      : undefined;
  const zoom =
    searchParams?.get('zoom') && !Number.isNaN(Number(searchParams.get('zoom')))
      ? Number(searchParams.get('zoom'))
      : undefined;

  // MapBox hooks
  useMapSelections(map);
  useMapCustomizationSelections(map);
  useMapFeatureHover(map);
  useActiveSource(map, MAP_BOX_LAYERS);
  useSelectionCentroid(map, allFeatures);
  useZoomSelect();
  useAdvancedSearchResultSelect();

  useHandleInitialMapboxZooming(mapContainer);

  // Adds the draw tool
  useEffect(() => {
    boundaryTransferRef.current = boundaryTransfer;
  }, [boundaryTransfer]);

  const loadMapBoxDraw = useCallback(
    (m) => {
      if (!m || !isDefined(isToolbarActive) || isToolbarActive) return;

      const drawToolControl = new MapboxDraw({
        clickBuffer: 16,
        controls: {
          combine_features: true,
          polygon: true,
          trash: true,
        },
        keybindings: true,
        modes: {
          ...MapboxDraw.modes,
          draw_circle: setupDrawCircleMode({
            setHoverToolTip: setText,
            setIsVisible,
          }),
          draw_pivot: setupDrawPivot({
            setText,
          }),
          draw_polygon: setupDrawPolygon({
            setIsVisible,
            setText,
          }),
          draw_rectangle: setupDrawRectangleDrag({
            setIsVisible,
            setText,
          }),
          static: StaticMode,
        },
        styles: getMapboxDrawStyles(),
        userProperties: true,
      });

      try {
        m.addControl(drawToolControl);
      } catch (err) {
        Logger.error(err);
      }

      dispatch(setDrawTool(drawToolControl));
    },
    [dispatch, isToolbarActive, setIsVisible, setText]
  );

  useEffect(() => {
    if (map && isMapInstanceLoaded) loadMapBoxDraw(map);
  }, [isMapInstanceLoaded, loadMapBoxDraw, map]);

  useEffect(() => {
    if (!drawTool) return;
    try {
      if (drawTool?.changeMode) {
        drawTool.changeMode('simple_select');
      }
    } catch (err) {
      Logger.error(err);
    }
  }, [drawTool, activeFileId]);

  useEffect(() => {
    if (!map) return;

    map.on('load', () => {
      addLayer(map, [LocationPinLayer]);
      try {
        if (!map.listImages().includes('location-pin')) {
          map.loadImage(locationPin.src).then((res) => {
            map.addImage('location-pin', res.data);
          });
        }
      } catch (err) {
        Logger.error(err);
      }
    });
  }, [map]);

  const updateURL = useCallback(async () => {
    if (!map) return;
    const page = pathname?.split('/')[2];
    if (!page) return;

    // Ignore these urls
    if (EXCLUDE_DYNAMIC_URL_LAYERS.includes(page as LayerIdEnums)) {
      return;
    }

    dynamicURl(
      page,
      map.getCenter(),
      map.getZoom(),
      pathname,
      params,
      searchParams,
      map
    );
  }, [map, pathname, params, searchParams]);

  const renderSharedParcels = useCallback(
    (boundaryTransferArray: AcresFeature[]) => {
      boundaryTransferArray.forEach((feature) => {
        const id =
          feature.id || generateUniqueId({ length: 10, useLetters: false });

        dispatch(
          addFeature({
            feature: { ...feature, id },
          })
        );

        if (drawTool && isBoundaryFeature(feature)) {
          drawTool.add({ ...feature, id, type: 'Feature' });
        }
      });
    },
    [dispatch, drawTool]
  );

  useEffect(() => {
    if (map && isMapInstanceLoaded && boundaryTransfer.length && drawTool) {
      let combinedFeatures: Feature<Polygon | MultiPolygon> | null = null;

      // Searching a place with only a centroid will crash
      // Will only come here if searching from the home page
      try {
        combinedFeatures = multiUnion(boundaryTransfer);
      } catch (err) {
        Logger.error(err);
      }

      if (!combinedFeatures) return;

      if (isPolygonic(combinedFeatures.geometry.type)) {
        const newFeature: Feature = JSON.parse(
          JSON.stringify(combinedFeatures)
        );

        newFeature.type = 'Feature';

        zoomToBounds(map, newFeature.geometry, false, undefined, true);

        if (boundaryTransfer.length && drawToolRef.current) {
          renderSharedParcels(boundaryTransfer as Feature[]);
        }
      }

      dispatch(clearBoundaryTransfer());
    }
  }, [
    isMapInstanceLoaded,
    boundaryTransfer,
    drawTool,
    map,
    dispatch,
    renderSharedParcels,
    drawToolRef,
  ]);

  useEffect(() => {
    if (!map) return () => {};

    map.on('moveend', updateURL);

    return () => {
      map.off('moveend', updateURL);
    };
  }, [map, drawToolRef, boundaryTransferRef, updateURL]);

  const handleMouseLeave = useCallback(() => {
    setIsCursorOverMap(false);
  }, [setIsCursorOverMap]);

  const handleMouseEnter = useCallback(() => {
    setIsCursorOverMap(true);
  }, [setIsCursorOverMap]);

  useEffect(() => {
    return () => {
      setIsMapInstanceLoaded(false);
    };
  }, [setIsMapInstanceLoaded]);

  if (!mapTilerAccessToken) {
    throw Error('Maptiler access token not found');
  }

  if (!accessToken || !mapboxBaseMap) {
    return null;
  }

  return (
    <div
      ref={mapContainer}
      className="flex h-full w-full grow"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Map
        mapLib={maptilersdk}
        dragRotate={false}
        doubleClickZoom={false}
        reuseMaps
        maxPitch={75}
        id="map"
        initialViewState={{
          latitude: lat ?? 39,
          longitude: lng ?? -96,
          zoom: zoom ?? MIN_2D_MODE_ZOOM,
        }}
        // NOTE: Token gets automatically added by MaptilerSDK
        mapStyle={`https://api.maptiler.com/maps/${mapboxBaseMap}/style.json`}
        maxZoom={MAX_2D_MODE_ZOOM}
        minZoom={MIN_2D_MODE_ZOOM}
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
        }}
        logoPosition="bottom-right"
        transformRequest={(url) => {
          if (
            url.startsWith('https://itasca') ||
            url.startsWith('https://penrose') ||
            url.startsWith('https://tipg') ||
            url.startsWith('https://kepler')
          ) {
            return {
              credentials: 'include',
              url,
            };
          }

          return { url };
        }}
        onLoad={(e) => {
          setIsMapInstanceLoaded(true);

          const mappy = e.target;

          // Hide the navigation control
          // eslint-disable-next-line no-underscore-dangle
          mappy._controlContainer.style.display = 'none';

          if (env('NEXT_PUBLIC_ENV') !== 'production') global.mapbox = mappy;

          const skyBlack = 'rgba(0, 0, 0, 1.0)';

          // Add atmosphere
          mappy.setSky({
            'horizon-color': skyBlack,
            'sky-color': skyBlack,
          });

          addLayer(mappy, [LocationPinLayer]);
          try {
            if (!mappy.listImages().includes('location-pin')) {
              mappy.loadImage(locationPin.src).then((res) => {
                if (res.data) {
                  mappy.addImage('location-pin', res.data);
                }
              });
            }
          } catch (err) {
            Logger.error(err);
          }

          mappy.resize();
        }}
      >
        <AllMapboxLayers />
      </Map>
    </div>
  );
};

export default MapBox;
